/* In this module, the 'any' type is often needed for inferred types, particularly as it relates to
   functions. */

import { uniq, sortBy } from "lodash";
import { z } from "zod";

import type * as attributes from "./attributes";
import type * as labels from "./labels";
import type * as model from "./model";
import type * as options from "./options";
import type * as subsets from "./subsets";
import type * as types from "./types";

import { humanizeList } from "../../formatting/humanize-list";

import { type EnumeratedLiterals } from "./exposed-types";

export * from "./exposed-types";

/**
 * Converts the provided value accessor in its original form to the form defined by the type
 * {@link LiteralsAccessor}.
 *
 * @example
 * // Returns "MY_ACCESSOR"
 * toLiteralAccessor("my-accessor");
 */
const toLiteralAccessor = <V extends string = string>(
  v: V,
): types.LiteralsAccessor<V> =>
  v
    .toUpperCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_") as types.LiteralsAccessor<V>;

const getAccessors = <V extends types.Literals>(
  data: V,
): types.LiteralsAccessors<V> => {
  if (Array.isArray(data)) {
    return {
      ...uniq(data).reduce(
        (prev: types.LiteralsAccessors<V>, curr: V[number] & string) => ({
          ...prev,
          [toLiteralAccessor(curr)]: curr,
        }),
        {} as types.LiteralsAccessors<V>,
      ),
    };
  }
  return Object.keys(data).reduce(
    (prev: types.LiteralsAccessors<V>, curr: string) =>
      ({
        ...prev,
        [toLiteralAccessor(curr)]: (data as types.LiteralsMapping)[curr],
      } as types.LiteralsAccessors<V>),
    {} as types.LiteralsAccessors<V>,
  );
};

const _getValues = <L extends types.Literals>(
  value: L,
  order: string[],
): readonly types.LiteralValue<L>[] => {
  if (Array.isArray(value)) {
    return value;
  } else {
    return sortBy(
      Object.keys(value).reduce((prev, k) => {
        const v: types.LiteralValue<L> = (
          value as { [key in string]: types.LiteralValue<L> }
        )[k];
        return [...prev, v];
      }, [] as types.LiteralValue<L>[]),
      (o) => order.indexOf(o),
    ) as readonly types.LiteralValue<L>[];
  }
};

const _getModel = <
  V extends types.Literals,
  O extends options.LiteralsOptions<V>,
>(
  data: V,
  accessors: types.LiteralsAccessors<V>,
  { getLabels, getAttributes }: O,
): model.LiteralsModel<O, types.LiteralValue<V>>[] => {
  const attrs = getAttributes?.(accessors);
  const lbs = getLabels?.(accessors);

  const withAttributes = <T>(obj: T, v: types.LiteralValue<V>) => {
    if (attrs !== undefined) {
      return {
        ...obj,
        ...attrs[v],
      };
    }
    return obj;
  };

  if (Array.isArray(data)) {
    const _data = data as ReadonlyArray<types.LiteralValue<V>>;
    if (lbs !== undefined) {
      return _data.map(
        (d) =>
          withAttributes(
            {
              value: d,
              label: lbs[d],
            },
            d,
          ) as model.LiteralsModel<O, types.LiteralValue<V>>,
      );
    }
    return _data.map(
      (d) =>
        withAttributes(
          {
            value: d,
          },
          d,
        ) as model.LiteralsModel<O, types.LiteralValue<V>>,
    );
  } else if (lbs !== undefined) {
    return Object.keys(data).map((k) => {
      const v = (data as { [key in string]: types.LiteralValue<V> })[k];
      return withAttributes(
        {
          value: v,
          label: lbs[v],
        },
        v,
      ) as model.LiteralsModel<O, types.LiteralValue<V>>;
    });
  }
  return Object.keys(data).map(
    (k) =>
      withAttributes(
        {
          value: (data as { [key in string]: types.LiteralValue<V> })[k],
        },
        (data as { [key in string]: types.LiteralValue<V> })[k],
      ) as model.LiteralsModel<O, types.LiteralValue<V>>,
  );
};

/**
 * A function that returns an enumerated literals instance, {@link EnumeratedLiterals}, associated
 * with the provided data, either as a readonly array of string values or a mapping of string
 * accessors to their associated values.
 *
 * Generally, an {@link EnumeratedLiterals} instance is defined as an object that is used to
 * represent the discrete, literal {@link string} values that a given variable can exhibit, by
 * providing both properties to access the discrete values themselves and a property to access
 * an {@link Array} of all possible discrete values.
 *
 * The enumerated literals instance, {@link EnumeratedLiterals}, behaves similarly to an enum-type,
 * but provides typeguards, validators and iterable-type methods & accessors which maintain the
 * order in which the values were originally provided.
 *
 * The enumerated literals instance, {@link EnumeratedLiterals}, can optionally be associated with
 * a set of labels, {@link LiteralsLabels}, that, when included, will expose additional methods on
 * the instance, and properties on the data that is associated with the instance.  Additionally,
 * the {@link EnumeratedLiterals} can contain subsets of values, and can also attribute values with
 * additional attributes.
 *
 * @see {EnumeratedLiterals}
 *
 * @example
 * const Permissions = enumeratedLiterals(["admin", "dev", "user"] as const)
 * Permissions.ADMIN // "admin"
 * Permissions.data // [{ value: "admin"}, {value: "dev"}, {value: "user"}]
 *
 * @example
 * const Permissions = enumeratedLiterals(
 *   ["admin", "dev", "user"] as const,
 *   labels: { admin: "Admin", dev: "Dev", user: "User" }
 * )
 * Permissions.getLabel(Permissions.ADMIN) // "Admin"
 * Permissions.data // [{ "id": "admin", "label": "Admin" }, ...]
 *
 * @example
 * const Permissions = enumeratedLiterals({ isAdmin: "admin", isDev: "dev" }
 * Permissions.ISADMIN // "admin"
 * PERMISSIONS.ISDEV // "dev"
 *
 * @param {V} data
 *   Either a read-only array of string, literal values that the variable is allowed to exhibit or
 *   a mapping of accessors to string, literal values that the variable is allowed to exhibit.
 *
 * @param {O} options
 *   Options for the {@link EnumeratedLiterals} instance.  These include the following:
 *
 *   1. getOrder
 *      A callback that will define the ordering of the values when the values are provided as a
 *      mapping.  @see {LiteralsOptions}
 *
 *   2. getLabels
 *      An optional callback that will define the labels that the values of the
 *      {@link EnumeratedLiterals} instance are associated with.
 *
 *   3. getAttributes
 *      An optional callback that will define additional attributes that the values of the
 *      {@link EnumeratedLiterals} instance are associated with.
 *
 *   4. getSubsets
 *      An optional callback that will define subsets of the values of the
 *      {@link EnumeratedLiterals} instance.
 *
 * @returns {@link EnumeratedLiterals<V, O>}
 */
export const enumeratedLiterals = <
  V extends types.Literals,
  O extends options.LiteralsOptions<V>,
>(
  _data: V,
  o: O,
): EnumeratedLiterals<V, O> => {
  const accessors = getAccessors<V>(_data);
  const order: types.LiteralValue<V>[] = Array.isArray(_data)
    ? [..._data]
    : o.getOrder?.(accessors) || [];

  const data = sortBy(_getModel<V, O>(_data, accessors, o), (d) =>
    order.indexOf(d.value as types.LiteralValue<V>),
  );

  const values = _getValues<V>(_data, order);

  const attrs = o.getAttributes?.(accessors) as attributes.AllAttributes<V, O>;

  const getAttribute = (
    attrs !== undefined
      ? <
          I extends types.LiteralValue<V>,
          N extends keyof attributes.Attributes<I, V, O>,
        >(
          value: I,
          name: N,
        ): attributes.Attributes<I, V, O>[N] => {
          /* These should be prevented by type checks, but just in case we will still throw an error
             here. */
          if (attrs[value] === undefined) {
            throw new Error(
              `The provided value '${value}' is not in the set of attributes!`,
            );
          } else if (attrs[value][name] === undefined) {
            const allowedAttributes = humanizeList(Object.keys(attrs[value]), {
              conjunction: "or",
              formatter: (v) => `'${v}'`,
            });
            throw new Error(
              `Invalid attribute '${String(
                name,
              )}' provided.  Attribute must be one of ${allowedAttributes}.`,
            );
          }
          return attrs[value][name];
        }
      : undefined
  ) as attributes.GetAttribute<V, O>;

  const getLabel = <I extends types.LiteralValue<V>>(
    id: I,
  ): labels.LiteralsLabels<V, O>[I] => {
    const ls = o.getLabels?.(accessors) as
      | labels.LiteralsLabels<V, O>
      | undefined;
    return (ls ? ls[id] : undefined) as labels.LiteralsLabels<V, O>[I];
  };

  const getSubset: subsets.GetSubset<V, O> = (<
    Ni extends subsets.SubsetName<V, O>,
  >(
    name: Ni,
  ): subsets.Subsets<V, O>[Ni] => {
    const ssets = o.getSubsets?.(accessors) as
      | subsets.Subsets<V, O>
      | undefined;
    return (ssets !== undefined ? ssets[name] : undefined) as subsets.Subsets<
      V,
      O
    >[Ni];
  }) as subsets.GetSubset<V, O>;

  const getSubsetModels: subsets.GetSubsetModel<V, O> = (<
    Ni extends subsets.SubsetName<V, O>,
  >(
    name: Ni,
  ): subsets.SubsetModel<Ni, V, O>[] | undefined => {
    const ssets = o.getSubsets?.(accessors);
    if (ssets) {
      const subset: types.LiteralValue<V>[] = (ssets as subsets.Subsets<V, O>)[
        name
      ];
      return data.filter((d) =>
        subset.includes(d.value as subsets.Subsets<V, O>[Ni][number]),
      ) as subsets.SubsetModel<Ni, V, O>[];
    }
    return undefined;
  }) as subsets.GetSubsetModel<V, O>;

  return {
    ...accessors,
    values,
    data,
    schema: z.enum(
      values as [types.LiteralValue<V>, ...types.LiteralValue<V>[]],
    ),
    getLabel,
    getAttribute,
    getSubset,
    getSubsetModels,
    getSafe<GO extends model.SafeGetterOptions>(
      this: EnumeratedLiterals<V, O>,
      v: unknown,
      safeGetterOpts: GO,
    ) {
      if (this.contains(v)) {
        return accessors[toLiteralAccessor(v)] as model.SafeGetterRT<
          types.LiteralValue<V>,
          GO
        >;
      } else if (safeGetterOpts.strict === false) {
        safeGetterOpts.onError?.();
        return null as model.SafeGetterRT<types.LiteralValue<V>, GO>;
      }
      safeGetterOpts.onError?.();
      return this.throwInvalidValueError(v);
    },
    getModel<I extends types.LiteralValue<V>>(this, v: I) {
      const result = this.data.find((d) => (d.value as I) === v);
      // This should be protected by type checks, but just in case we will throw an error here.
      if (result === undefined) {
        return this.throwInvalidValueError(v);
      }
      return result as model.ModelForValue<I, V, O>;
    },
    getModelSafe<GO extends model.SafeGetterOptions>(
      this: EnumeratedLiterals<V, O>,
      v: unknown,
      safeGetterOpts: GO,
    ) {
      if (this.contains(v)) {
        return this.getModel(v);
      } else if (safeGetterOpts.strict === false) {
        safeGetterOpts.onError?.();
        return null as model.SafeGetterRT<
          EnumeratedLiterals<V, O>["data"][number],
          GO
        >;
      }
      safeGetterOpts.onError?.();
      return this.throwInvalidValueError(v);
    },
    contains(
      this: EnumeratedLiterals<V, O>,
      v: unknown,
    ): v is types.LiteralValue<V> {
      return this.schema.safeParse(v).success;
    },
    throwInvalidValueError: (v: unknown): never => {
      const message = `The value ${v} is not valid, it must be one of ${data
        .map((d) => d.value)
        .join(", ")}.`;
      throw new Error(message);
    },
    assert(this: EnumeratedLiterals<V, O>, v: unknown): types.LiteralValue<V> {
      if (this.contains(v)) {
        return v;
      }
      return this.throwInvalidValueError(v);
    },
  };
};
